// trackEat — Landing per `/accept-invite?token=XXX`
//
// Flusso:
// 1. Estrae token dalla query string e lo salva in localStorage (sopravvive al
//    redirect signin/signup)
// 2. Se NON loggato → mostra signup/signin con email pre-compilata (se possiamo
//    estrarre la email associata al token tramite chiamata pubblica? No, non
//    possiamo — l'OSA conosce l'email. Lasciamo l'utente digitarla)
// 3. Se loggato → chiama edge function `accept-invite` con il token
// 4. Successo → redirect a /today (l'app sa già del nuovo tenant via JWT
//    refresh dopo accept)

import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { mailOutline, alertCircleOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { getSupabase } from '../../lib/supabase.js';
import { GoogleSignInButton } from '../../components/GoogleSignInButton.js';
import { toUserMessage } from '../../lib/errors.js';

const TOKEN_STORAGE_KEY = 'trackeat.pending_invite_token';

export function AcceptInvitePage(): JSX.Element {
  const location = useLocation();
  const history = useHistory();
  const { session, profile } = useAuth();
  const [status, setStatus] = useState<'idle' | 'accepting' | 'ok' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('token');
    if (t) {
      try { localStorage.setItem(TOKEN_STORAGE_KEY, t); } catch { /* noop */ }
      setToken(t);
    } else {
      try {
        const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (stored) setToken(stored);
      } catch { /* noop */ }
    }
  }, [location.search]);

  useEffect(() => {
    if (!token || !session || status !== 'idle') return;
    void (async () => {
      setStatus('accepting');
      setError(null);
      try {
        const { data, error: invokeErr } = await getSupabase().functions.invoke('accept-invite', {
          body: { token },
        });
        if (invokeErr) throw invokeErr;
        const result = data as { tenant_id?: string };
        if (!result.tenant_id) throw new Error('Risposta accept-invite incompleta');
        try { localStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* noop */ }
        // Forza refresh sessione per ottenere JWT con il nuovo tenant nei claims.
        await getSupabase().auth.refreshSession();
        setStatus('ok');
        // Reindirizza alla home dopo 1.5s.
        setTimeout(() => history.replace('/today'), 1500);
      } catch (err) {
        setStatus('error');
        setError(toUserMessage(err));
      }
    })();
  }, [token, session, status, history]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonBackButton defaultHref="/auth/signin" /></IonButtons>
          <IonTitle>Accetta invito</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div style={{ maxWidth: 480, margin: '0 auto', paddingTop: 32 }}>
          {!token && (
            <div style={{ textAlign: 'center' }}>
              <IonIcon icon={alertCircleOutline} style={{ fontSize: 48, color: '#dc2626' }} />
              <h2 style={{ fontSize: 18, marginTop: 12 }}>Token mancante</h2>
              <IonText color="medium">
                <p>Il link di invito non contiene un token valido. Chiedi al titolare di rigenerarlo.</p>
              </IonText>
            </div>
          )}
          {token && !session && (
            <div style={{ textAlign: 'center' }}>
              <IonIcon icon={mailOutline} style={{ fontSize: 48, color: '#0066cc' }} />
              <h2 style={{ fontSize: 18, marginTop: 12 }}>Sei stato invitato in TrackEAT</h2>
              <IonText color="medium">
                <p>
                  Per completare l'iscrizione, registrati o accedi con la stessa
                  email a cui ti hanno mandato il link. Tornerai automaticamente
                  qui dopo il login.
                </p>
              </IonText>
              <IonButton expand="block" routerLink="/auth/signup" style={{ marginTop: 16 }}>
                Registrati
              </IonButton>
              <IonButton expand="block" fill="outline" routerLink="/auth/signin" style={{ marginTop: 8 }}>
                Ho già un account
              </IonButton>
              <div style={{ textAlign: 'left' }}>
                <GoogleSignInButton />
              </div>
            </div>
          )}
          {token && session && status === 'accepting' && (
            <div style={{ textAlign: 'center' }}>
              <IonSpinner name="dots" style={{ width: 48, height: 48 }} />
              <p style={{ marginTop: 12, color: '#475569' }}>Accettazione invito in corso…</p>
            </div>
          )}
          {status === 'ok' && (
            <div style={{ textAlign: 'center' }}>
              <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 48, color: '#15803d' }} />
              <h2 style={{ fontSize: 18, marginTop: 12 }}>Benvenuto in TrackEAT!</h2>
              <IonText color="medium">
                <p>Sei stato aggiunto al ristorante. Ti reindirizziamo alla home…</p>
              </IonText>
            </div>
          )}
          {status === 'error' && (
            <div style={{ textAlign: 'center' }}>
              <IonIcon icon={alertCircleOutline} style={{ fontSize: 48, color: '#dc2626' }} />
              <h2 style={{ fontSize: 18, marginTop: 12 }}>Invito non valido</h2>
              <IonText color="danger">
                <p>{error ?? 'Errore sconosciuto'}</p>
              </IonText>
              <IonText color="medium">
                <p style={{ fontSize: 12 }}>
                  Possibili cause: l'email con cui ti sei registrato non corrisponde a
                  quella dell'invito, l'invito è scaduto (validi 14 giorni) o è già
                  stato usato. Chiedi al titolare di mandartene uno nuovo.
                </p>
              </IonText>
              <IonButton expand="block" fill="outline" onClick={() => history.replace('/auth/signin')} style={{ marginTop: 16 }}>
                Torna al login
              </IonButton>
            </div>
          )}
          {status === 'idle' && profile?.tenants && profile.tenants.length > 0 && token && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <p style={{ fontSize: 12, color: '#94a3b8' }}>
                Sei già registrato come membro di un ristorante. L'invito ti
                aggiungerà come membro di un ristorante aggiuntivo.
              </p>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
